import { lazy, Suspense } from 'react'
import { useSearchParams } from 'react-router-dom'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'

const AuditLogsPage = lazy(() =>
  import('@/pages/audit-logs').then((m) => ({ default: m.AuditLogsPage }))
)
const DecisionLogsPage = lazy(() =>
  import('@/pages/decision-logs').then((m) => ({ default: m.DecisionLogsPage }))
)
const ReportsPage = lazy(() =>
  import('@/pages/reports').then((m) => ({ default: m.ReportsPage }))
)

const TABS = [
  { value: 'audit-logs', label: 'Audit Logs' },
  { value: 'access-decisions', label: 'Access Decisions' },
  { value: 'reports', label: 'Reports' },
] as const

function TabFallback() {
  return (
    <div className="space-y-4 p-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-64 w-full" />
    </div>
  )
}

export function AnalyticsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedTab = searchParams.get('tab')
  const activeTab = TABS.some((t) => t.value === requestedTab) ? requestedTab! : 'audit-logs'

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value }, { replace: true })
  }

  return (
    <>
      <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <h1 className="text-lg font-semibold">Analytics</h1>
      </header>

      <div className="flex flex-1 flex-col p-4">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="flex-1">
          <TabsList>
            {TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="audit-logs" className="mt-4">
            <Suspense fallback={<TabFallback />}>
              <AuditLogsPage />
            </Suspense>
          </TabsContent>

          <TabsContent value="access-decisions" className="mt-4">
            <Suspense fallback={<TabFallback />}>
              <DecisionLogsPage />
            </Suspense>
          </TabsContent>

          <TabsContent value="reports" className="mt-4">
            <Suspense fallback={<TabFallback />}>
              <ReportsPage />
            </Suspense>
          </TabsContent>
        </Tabs>
      </div>
    </>
  )
}
